/**
 * The Empfehlungen tab, in the design's shape (COL-533).
 *
 * V2 mounted V1's `ReferralsPanel` — 1554 lines of a different design: a
 * gradient hero, a 10-referral progress meter, three stat tiles and an
 * accordion. The export is one beige banner with the code, a four-step rail
 * and a list. Restyling V1's panel in place would change V1 too, so this is a
 * presentation layer over the SAME two queries.
 *
 * The query keys are V1's, deliberately — `["referral-code", uid]` and
 * `["my-referrals", uid]`. Same cache entries, same RPC, so the two dashboards
 * cannot disagree about how many referrals a tenant has or what they earned.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Check, Share2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import {
  EMPTY_REFERRALS,
  formatEuroFromCents,
  REFERRAL_REWARD_CENTS,
  type MyReferrals,
  stageLabel,
} from "@/lib/referrals";
import { supabase } from "@/lib/supabase";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";

export default function TenantReferrals() {
  const { user } = useAuth();
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );
  const [copied, setCopied] = useState<"code" | "link" | null>(null);

  const { data: code, isError: codeFailed } = useQuery<string | null>({
    // V1's key, so one fetch serves both dashboards.
    queryKey: ["referral-code", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_code")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (
        (data as { referral_code?: string | null } | null)?.referral_code ??
        null
      );
    },
  });

  const { data: referrals = EMPTY_REFERRALS, isError: listFailed } =
    useQuery<MyReferrals>({
      queryKey: ["my-referrals", user?.id],
      enabled: !!user?.id,
      queryFn: async () => {
        const { data, error } = await supabase.rpc("my_referrals");
        if (error) throw new Error(error.message);
        // jsonb: MyReferrals, or null.
        return (data as unknown as MyReferrals | null) ?? EMPTY_REFERRALS;
      },
    });

  const reward = formatEuroFromCents(REFERRAL_REWARD_CENTS, language);
  /*
    `/properties?ref=` attributed nothing.

    The ONLY reader of the `ref` query parameter is BookingConfirmation, which
    seeds the referral field from it (see REFERRAL_URL_PARAM in lib/referrals).
    `/properties` never reads it, and nothing persists it across the navigation
    to an application — so every friend who followed the old link applied with
    an empty referral field and the referrer earned nothing. Silent: the link
    opened a perfectly normal page.

    V1's model is the correct one: a shared ROOM links to that room's
    application page via `roomApplyUrl(room.id, code)`, and a bare code is
    shared AS a code. This banner has no room, so it shares the code — which
    `referralShareText` already carries — alongside a plain listing link.
  */
  const shareUrl = `${window.location.origin}/properties`;
  const shareText = d.referralShareText.replace("{code}", code ?? "");
  /*
    Copy the whole message, not the bare URL. The URL carries no code, so
    copying it alone would hand the tenant something that looks shareable and
    loses them the reward — the same failure in a different shape.
  */
  const shareMessage = `${shareText} ${shareUrl}`;

  const copy = async (what: "code" | "link", value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard is permission-gated; a silent no-op beats a thrown dialog.
    }
  };

  const steps = [
    { n: 1, title: d.referralStep1, body: d.referralStep1Body },
    { n: 2, title: d.referralStep2, body: d.referralStep2Body },
    { n: 3, title: d.referralStep3, body: d.referralStep3Body },
    {
      n: 4,
      title: d.referralStep4.replace("{amount}", reward),
      body: d.referralStep4Body,
    },
  ];

  return (
    <div className="space-y-6">
      {/* The code banner. */}
      <Card className="rounded-[20px] border-0 bg-muted/60 p-6 sm:p-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">
              {d.referralHeadline.replace("{amount}", reward)}
            </p>
            {codeFailed ? (
              // Not a blank code: a tenant copying an empty string and sending
              // it to a friend is worse than being told it failed.
              <p className="mt-2 text-destructive">{d.referralCodeFailed}</p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[2rem]">
                  {code ?? "—"}
                </span>
                {code ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="rounded-full"
                    onClick={() => copy("code", code)}
                  >
                    {copied === "code" ? (
                      <>
                        <Check className="mr-1.5 h-3.5 w-3.5" />
                        {d.referralCopied}
                      </>
                    ) : (
                      d.referralCopy
                    )}
                  </Button>
                ) : null}
              </div>
            )}
          </div>

          {code ? (
            <div className="flex flex-wrap gap-2.5">
              <Button asChild className="rounded-full">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  {d.referralShareWhatsApp}
                </a>
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => copy("link", shareMessage)}
              >
                {copied === "link" ? (
                  <>
                    <Check className="mr-1.5 h-4 w-4" />
                    {d.referralCopied}
                  </>
                ) : (
                  <>
                    <Copy className="mr-1.5 h-4 w-4" />
                    {d.referralCopyLink}
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {/* How it works — a four-step rail, as the export draws it. */}
      <Card className="rounded-[20px] border-0 bg-muted/60 p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-foreground">
          {d.referralHowItWorks}
        </h2>
        <ol className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step) => (
            <li key={step.n} className="relative min-w-0">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-sm font-semibold text-background">
                {step.n}
              </span>
              <p className="mt-3 font-semibold text-foreground">{step.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
            </li>
          ))}
        </ol>
      </Card>

      {/* Your referrals. */}
      <Card className="rounded-[20px] p-6 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border/60 pb-4">
          <h2 className="text-lg font-semibold text-foreground">
            {d.referralYours}
          </h2>
          <span className="text-sm text-muted-foreground">
            {d.referralSavedSoFar.replace(
              "{amount}",
              formatEuroFromCents(referrals.earned_cents, language)
            )}
          </span>
        </div>

        {/*
          A failed RPC defaults to EMPTY_REFERRALS, which renders exactly like a
          genuine new referrer. Saying so beats telling a tenant with nine
          successful referrals that they have none, in the calm voice of a
          correct answer.
        */}
        {listFailed ? (
          <p className="pt-4 text-destructive">{d.referralListFailed}</p>
        ) : referrals.items.length === 0 ? (
          <p className="pt-4 text-muted-foreground">{d.referralNobodyYet}</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {/*
              `referred_name` and `stage`, which is what `my_referrals`
              actually returns (see `ReferralItem` in lib/referrals.ts). This
              rendered `referee_name`/`status` — fields that do not exist — so
              every row showed a dash with a blank status, losing information
              V1 displayed. The `: any` on the map hid it from the compiler,
              so the type annotation is gone too.
            */}
            {referrals.items.map((item, i) => (
              <li
                key={String(item?.id ?? i)}
                className="flex flex-wrap items-center justify-between gap-3 py-4"
              >
                <span className="min-w-0 truncate font-medium text-foreground">
                  {item?.referred_name || "\u2014"}
                </span>
                <span className="text-sm text-muted-foreground">
                  {item?.stage ? stageLabel(item.stage, language) : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
